import { useParams } from 'react-router-dom';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { LoadingState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { StatusBadge } from '@/components/StatusBadge';
import { useState } from 'react';
import { PortalUsersPanel } from './PortalUsersPanel';
import { ReadinessPanel, type FixAction } from './ReadinessPanel';
import { ContractPanel } from './ContractPanel';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatDate } from '@/i18n/format';
import type { TranslationKey } from '@/i18n/dictionaries/en';

const TAB_KEYS = ['overview', 'contract', 'stock', 'inbounds', 'orders', 'returns', 'billing', 'portalUsers', 'activity'] as const;
type TabKey = (typeof TAB_KEYS)[number];
const TAB_LABEL_KEYS: Record<TabKey, TranslationKey> = {
  overview: 'overview.title',
  contract: 'sellerDetail.tabContract',
  stock: 'sellerDetail.tabStock',
  inbounds: 'nav.inbounds',
  orders: 'nav.orders',
  returns: 'nav.returns',
  billing: 'nav.billing',
  portalUsers: 'sellerDetail.tabPortalUsers',
  activity: 'sellerDetail.tabActivity',
};

export function SellerDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [tab, setTab] = useState<TabKey>('overview');
  // The readiness chain re-reads after any change; a fix button opens the contract form / the Portal invite right here.
  const [readinessVersion, setReadinessVersion] = useState(0);
  const [openContractForm, setOpenContractForm] = useState<{ n: number; contractId: string | null }>({ n: 0, contractId: null });
  const [inviteOpen, setInviteOpen] = useState(false);
  function fix(action: FixAction, contractId: string | null = null): void {
    if (action === 'PORTAL') {
      setInviteOpen(true);
      setTab('portalUsers');
      return;
    }
    setTab('contract');
    setOpenContractForm((prev) => ({ n: prev.n + 1, contractId }));
  }
  const result = useApi(() => endpoints.seller(id!), [id]);
  const { t, locale } = useLanguage();

  if (result.status === 'loading') return <LoadingState />;
  if (result.status === 'error') return <ErrorBanner error={result.error} onRetry={result.reload} />;

  const { owner, contracts, stockByWarehouse, openOrders, openInbounds } = result.data;
  const ownerRow = owner as { name: string; code: string; active: boolean };

  return (
    <div>
      <div className="page-header">
        <h1>{ownerRow.name}</h1>
        <span className="sub">{ownerRow.code}</span>
      </div>

      <ReadinessPanel ownerId={id!} version={readinessVersion} onFix={fix} />

      <div className="tabs">
        {TAB_KEYS.map((key) => (
          <button key={key} type="button" className={tab === key ? 'active' : ''} onClick={() => setTab(key)}>
            {t(TAB_LABEL_KEYS[key])}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="card">
          <dl className="kv">
            <dt>{t('common.status')}</dt>
            <dd>{ownerRow.active ? t('sellers.active') : t('sellers.inactive')}</dd>
            <dt>{t('sellerDetail.contracts')}</dt>
            <dd>{contracts.length}</dd>
            <dt>{t('sellerDetail.openOrders')}</dt>
            <dd>{openOrders.length}</dd>
            <dt>{t('sellerDetail.openInbounds')}</dt>
            <dd>{openInbounds.length}</dd>
          </dl>
        </div>
      )}

      {tab === 'contract' && <ContractPanel ownerId={id!} openRequest={openContractForm} onChanged={() => setReadinessVersion((n) => n + 1)} />}

      {tab === 'stock' && (
        <div className="card">
          {stockByWarehouse.length === 0 ? (
            <p>{t('sellerDetail.noStock')}</p>
          ) : (
            <div className="table-scroll"><table>
              <thead>
                <tr>
                  <th>{t('inbounds.colWarehouse')}</th>
                  <th>{t('sellerDetail.skus')}</th>
                  <th>{t('inventory.colPhysical')}</th>
                  <th>{t('inventory.colReserved')}</th>
                  <th>{t('inventory.colAvailable')}</th>
                  <th>{t('inventory.summary.shipped')}</th>
                </tr>
              </thead>
              <tbody>
                {stockByWarehouse.map((row, i) => {
                  const r = row as Record<string, unknown>;
                  return (
                    <tr key={i}>
                      <td>{String(r.warehouse_name)}</td>
                      <td>{String(r.sku_count)}</td>
                      <td>{String(r.qty_on_hand)}</td>
                      <td>{String(r.qty_reserved)}</td>
                      <td>{String(r.qty_available)}</td>
                      <td>{String(r.qty_shipped)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          )}
        </div>
      )}

      {tab === 'inbounds' && (
        <div className="card">
          {openInbounds.length === 0 ? (
            <p>{t('sellerDetail.noOpenInbounds')}</p>
          ) : (
            <div className="table-scroll"><table>
              <thead>
                <tr>
                  <th>{t('sellerDetail.number')}</th>
                  <th>{t('inboundDetail.kind')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('inbounds.colExpected')}</th>
                </tr>
              </thead>
              <tbody>
                {openInbounds.map((row) => {
                  const r = row as Record<string, unknown>;
                  return (
                    <tr key={String(r.id)} onClick={() => (window.location.href = `/inbounds/${r.id}`)}>
                      <td>{String(r.number)}</td>
                      <td>{String(r.kind)}</td>
                      <td>
                        <StatusBadge value={String(r.state)} />
                      </td>
                      <td>{r.expected_at ? formatDate(String(r.expected_at), locale) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          )}
        </div>
      )}

      {tab === 'orders' && (
        <div className="card">
          {openOrders.length === 0 ? (
            <p>{t('sellerDetail.noOpenOrders')}</p>
          ) : (
            <div className="table-scroll"><table>
              <thead>
                <tr>
                  <th>{t('sellerDetail.number')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('orders.colDue')}</th>
                </tr>
              </thead>
              <tbody>
                {openOrders.map((row) => {
                  const r = row as Record<string, unknown>;
                  return (
                    <tr key={String(r.id)} onClick={() => (window.location.href = `/shipments/${r.id}`)}>
                      <td>{String(r.number)}</td>
                      <td>
                        <StatusBadge value={String(r.state)} />
                      </td>
                      <td>{r.due_at ? formatDate(String(r.due_at), locale) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          )}
        </div>
      )}

      {tab === 'returns' && (
        <div className="card">
          <p className="sub">{t('sellerDetail.seeReturnsSection')}</p>
        </div>
      )}

      {tab === 'billing' && (
        <div className="card">
          {contracts.length === 0 ? (
            <p>{t('sellerDetail.noContractsOnFile')}</p>
          ) : (
            <div className="table-scroll"><table>
              <thead>
                <tr>
                  <th>{t('common.status')}</th>
                  <th>{t('billing.currency')}</th>
                  <th>{t('billing.legalEntity')}</th>
                  <th>{t('billing.tariff')}</th>
                  <th>{t('sellerDetail.validFrom')}</th>
                </tr>
              </thead>
              <tbody>
                {contracts.map((row, i) => {
                  const r = row as Record<string, unknown>;
                  return (
                    <tr key={i}>
                      <td>
                        <StatusBadge value={String(r.state)} />
                      </td>
                      <td>{String(r.currency)}</td>
                      <td>{r.legal_entity_name ? String(r.legal_entity_name) : '—'}</td>
                      <td>{r.tariff_name ? String(r.tariff_name) : t('common.none')}</td>
                      <td>{r.valid_from ? formatDate(String(r.valid_from), locale) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          )}
        </div>
      )}

      {tab === 'portalUsers' && <PortalUsersPanel ownerId={id!} openInvite={inviteOpen} onChanged={() => setReadinessVersion((n) => n + 1)} />}

      {tab === 'activity' && (
        <div className="card">
          <p className="sub">{t('sellerDetail.activityNotImplemented')}</p>
        </div>
      )}
    </div>
  );
}
